import { todayDateRangeInUserTimeZone } from '@/helpers';
import { cloneDeep } from 'lodash-es';
import { NotificationItem } from '@/models/notification';
import { createNotificationPageHeaders } from '@/pages/admin/notifications/NotificationPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { notificationService } from '@/services/notificationService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { IPageOptions } from '@/types';
import type { INotificationFilters } from '@/types/notification';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'created_at', order: 'desc' as const }];

function createNotificationStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<NotificationItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const filters = reactive<INotificationFilters>({
        notificationDate: {
            value: todayDateRangeInUserTimeZone(),
            title: 'notifications.dateFilter',
            allowSaveState: false,
            disableClear: true,
        },
        notificationTypeId: {
            value: null,
            title: 'notifications.typeFilter',
            allowSaveState: true,
        },
        notificationPlatformId: {
            value: null,
            title: 'notifications.platformFilter',
            allowSaveState: true,
        },
        notificationCountryId: {
            value: null,
            title: 'notifications.countryFilter',
            allowSaveState: true,
        },
        notificationUserId: {
            value: null,
            title: 'notifications.createdByFilter',
            allowSaveState: true,
        },
        notificationRecipientId: {
            value: null,
            title: 'notifications.recipientFilter',
            allowSaveState: true,
        },
        notificationId: {
            value: null,
            title: 'notifications.idFilter',
            allowSaveState: false,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 100,
        page: 1,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        filters.notificationDate.value = todayDateRangeInUserTimeZone();
        filters.notificationTypeId.value = null;
        filters.notificationPlatformId.value = null;
        filters.notificationCountryId.value = null;
        filters.notificationUserId.value = null;
        filters.notificationRecipientId.value = null;
        filters.notificationId.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createNotificationPageHeaders());

    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title) && !header.hidden)
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'notification',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const filtersToSend = cloneDeep(filters);

            const params = {
                filters: filtersToSend,
                options: {
                    sortBy: options.value.sortBy,
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['NotPlayer', 'Country'],
            };

            const response = await notificationService.list(params);

            items.value = response.data.rows.map((row) => new NotificationItem(row));
            total.value = response.data.total;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    async function patchBoolField(item: NotificationItem, modelKey: 'isActive' | 'isDismissed', value: 0 | 1, apiField: string) {
        const oldValue = item[modelKey];
        item[modelKey] = value;
        triggerRef(items);

        try {
            const response = await notificationService.update({ rows: [{ id: item.id, [apiField]: value }] });
            if (response.data.success === false) {
                item[modelKey] = oldValue;
                triggerRef(items);
            }
        } catch {
            item[modelKey] = oldValue;
            triggerRef(items);
        }
    }

    async function deleteNotification(id: number) {
        await notificationService.delete(id);
        await loadData();
    }

    // --- return ---
    return {
        items,
        total,
        loading,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        options,
        headers,
        availableColumns,
        hiddenColumns,
        tableHeaders,
        resetFilters,
        reloadFiltersFromStorage,
        loadData,
        updateOptions,
        clearData,
        patchBoolField,
        deleteNotification,
        hasAdd: true,
        hasRefresh: true,
    };
}

export function useNotificationStore(storeId: string = 'notification') {
    const useStore = defineStore(storeId, () => createNotificationStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'notification', defaultSortBy),
    });
    return useStore();
}

export type NotificationStoreSetup = ReturnType<typeof useNotificationStore>;
